import React from 'react';

/* ============================================================================ */
/* DataTable — generic, themable, accessible table for the dashboard pages.    */
/* ============================================================================ */

export function DataTable({ columns, rows, emptyText = 'No data yet.', testid = 'dash-table' }) {
  if (!rows || rows.length === 0) {
    return (
      <div data-testid={`${testid}-empty`} style={{
        padding: 48, textAlign: 'center',
        color: 'var(--dash-text-muted)',
        fontSize: 13,
      }}>
        {emptyText}
      </div>
    );
  }
  return (
    <div data-testid={testid} style={{ overflow: 'auto', borderRadius: 12, border: '1px solid var(--dash-border)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: 'var(--dash-bg-card)' }}>
        <thead>
          <tr style={{ background: 'rgba(15,15,30,0.025)' }}>
            {columns.map(c => (
              <th key={c.key} style={{
                textAlign: 'left', padding: '12px 16px',
                color: 'var(--dash-text-muted)', fontWeight: 600,
                fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase',
                borderBottom: '1px solid var(--dash-border)',
              }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{
              borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--dash-border)',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(15,15,30,0.02)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              {columns.map(c => (
                <td key={c.key} style={{
                  padding: '12px 16px', color: 'var(--dash-text)',
                  whiteSpace: c.nowrap ? 'nowrap' : 'normal',
                }}>
                  {c.render ? c.render(r) : (r[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ============================================================================ */
/* KPICard — large number + label + trend pill.                                */
/* ============================================================================ */

export function KPICard({ label, value, prev, format = 'number', testid }) {
  const num = typeof value === 'number' ? value : 0;
  const numPrev = typeof prev === 'number' ? prev : null;
  const delta = numPrev !== null && numPrev > 0 ? ((num - numPrev) / numPrev) * 100 : null;
  const fmt = (v) => {
    if (format === 'currency') return `€${v.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    if (format === 'percent') return `${v.toFixed(1)}%`;
    return v.toLocaleString('fr-FR');
  };
  const positive = delta !== null && delta >= 0;
  return (
    <div data-testid={testid} style={{
      padding: 20,
      background: 'var(--dash-bg-card)',
      border: '1px solid var(--dash-border)',
      borderRadius: 'var(--dash-radius)',
      transition: 'all 0.2s',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.10em',
        textTransform: 'uppercase', color: 'var(--dash-text-muted)',
        marginBottom: 12,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 30, fontWeight: 700, color: 'var(--dash-text)',
        letterSpacing: '-0.02em', lineHeight: 1.1,
        fontFamily: 'var(--dash-font-display)',
      }}>
        {fmt(num)}
      </div>
      {delta !== null && (
        <div style={{
          marginTop: 10,
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '3px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
          background: positive ? 'rgba(124,58,237,0.12)' : 'rgba(255,95,87,0.10)',
          color: positive ? '#048264' : '#C0392B',
        }}>
          {positive ? '↑' : '↓'} {Math.abs(delta).toFixed(1)}%
        </div>
      )}
    </div>
  );
}

/* ============================================================================ */
/* StatusPill                                                                   */
/* ============================================================================ */

export function StatusPill({ value }) {
  const map = {
    paid: ['#048264', 'rgba(124,58,237,0.12)'],
    active: ['#048264', 'rgba(124,58,237,0.12)'],
    live: ['#048264', 'rgba(124,58,237,0.12)'],
    sent: ['#048264', 'rgba(124,58,237,0.12)'],
    pending: ['#B47A00', 'rgba(255,176,32,0.14)'],
    scheduled: ['#B47A00', 'rgba(255,176,32,0.14)'],
    trial: ['#B47A00', 'rgba(255,176,32,0.14)'],
    draft: ['#6B6B7B', 'rgba(15,15,30,0.08)'],
    refunded: ['#C0392B', 'rgba(255,95,87,0.10)'],
    churned: ['#C0392B', 'rgba(255,95,87,0.10)'],
  };
  const [color, bg] = map[value] || ['#6B6B7B', 'rgba(15,15,30,0.08)'];
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      fontSize: 11, fontWeight: 700, color, background: bg,
      textTransform: 'capitalize',
    }}>
      {value || '—'}
    </span>
  );
}

/* ============================================================================ */
/* SectionHeader                                                                */
/* ============================================================================ */

export function SectionHeader({ title, subtitle, actions }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
      gap: 16, marginBottom: 22,
    }}>
      <div>
        <h1 style={{
          margin: 0, fontSize: 26, fontWeight: 700,
          color: 'var(--dash-text)', letterSpacing: '-0.02em',
          fontFamily: 'var(--dash-font-display)',
        }}>{title}</h1>
        {subtitle && (
          <div style={{ marginTop: 6, fontSize: 13, color: 'var(--dash-text-muted)' }}>
            {subtitle}
          </div>
        )}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
    </div>
  );
}
